import { isOrganizationReference } from '@traceio-org/platform-contracts';
/**
 * What a destination remembers, in this browser, about its last granted sign-in: that there was one, and the
 * Auth0 organization it was in (null for Trace Admin, which signs in without one). With it a new tab first
 * signs in directly through the identity provider's SSO session (prompt=none) instead of a trip through the
 * account hub (ensureDestinationAccess, step b), which saves three page loads when the person is signed in.
 *
 * Security relevant, so spelled out: it holds no token, only an organization id the destination itself read from
 * a token that platform-api or the product backend just accepted. localStorage of this origin is written only by
 * this origin's own scripts, unlike the address bar (a link can carry any trace_organization), so it can steer a
 * silent sign-in. It decides nothing: Auth0 refuses an organization the person is not a member of, and the access
 * check that follows still decides. A refused silent sign-in falls back to the hub trip.
 */
export const REMEMBERED_SIGN_IN_KEY = 'trace.destination.lastSignIn';
/** Older than this, a remembered sign-in is ignored (a new tab then goes through the hub as before). */
export const REMEMBERED_SIGN_IN_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** The origin's localStorage, or null where the browser refuses it. */
export function defaultRememberedStorage() {
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    }
    catch {
        return null;
    }
}
/** The remembered sign-in when well formed and recent; anything else reads as none. */
export function readRememberedSignIn(storage, now) {
    let raw;
    try {
        raw = storage.getItem(REMEMBERED_SIGN_IN_KEY);
    }
    catch {
        return null;
    }
    if (raw === null)
        return null;
    let value;
    try {
        value = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return null;
    const record = value;
    const at = record.at;
    if (typeof at !== 'number' || !Number.isFinite(at) || at > now + 60_000 || now - at >= REMEMBERED_SIGN_IN_MAX_AGE_MS) {
        return null;
    }
    if (record.organization === null)
        return { organization: null, at };
    return isOrganizationReference(record.organization) ? { organization: record.organization, at } : null;
}
export function writeRememberedSignIn(storage, remembered) {
    try {
        storage.setItem(REMEMBERED_SIGN_IN_KEY, JSON.stringify(remembered));
    }
    catch {
        // Quota or privacy settings: the next tab simply goes through the hub.
    }
}
export function forgetRememberedSignIn(storage) {
    try {
        storage.removeItem(REMEMBERED_SIGN_IN_KEY);
    }
    catch {
        // Same as above.
    }
}
//# sourceMappingURL=remembered.js.map